import logger from '@/utils/logger';

import { Navigate } from 'react-router-dom';

import { AdminRoute } from '../../components/AdminRoute';
import { RestrictedBoardMemberRoute } from '../../components/RestrictedBoardMemberRoute';
import { PlaygroundRoute } from '../../pages/playground/PlaygroundRoute';
import { ProtectedRoute } from '../guards';
import {
  AdminAccountingPage,
  AdminAgentSurfacePage,
  AdminLlmCallsPage,
  AdminNotificationsPage,
  AdminPaymentsPage,
  AdminPipelinesPage,
  AdminRfpTemplatesPage,
  AdminTasksPage,
  ChartLinesPage,
  ExceptionQueuePage,
  IncomeChargeMappingPage,
  IncomeScheduler,
  OnboardingPage,
  OrganizationPage,
  PaymentsExamplePage,
  PlaygroundPage,
} from '../lazyPages';

export const adminLayoutChildren = [
  {
    path: 'admin/notifications',
    Component: () => (
      <AdminRoute>
        <AdminNotificationsPage />
      </AdminRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Admin Notifications route');
      return null;
    },
  },
  {
    path: 'admin/agent-surface',
    Component: () => (
      <AdminRoute>
        <AdminAgentSurfacePage />
      </AdminRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Admin Agent Surface route');
      return null;
    },
  },
  {
    path: 'admin/llm-calls',
    Component: () => (
      <AdminRoute>
        <AdminLlmCallsPage />
      </AdminRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Admin LLM Calls route');
      return null;
    },
  },
  {
    path: 'admin/pipelines',
    Component: () => (
      <AdminRoute>
        <AdminPipelinesPage />
      </AdminRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Admin Pipelines route');
      return null;
    },
  },
  {
    path: 'admin/tasks',
    Component: () => (
      <AdminRoute>
        <AdminTasksPage />
      </AdminRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Admin Tasks route');
      return null;
    },
  },
  {
    path: 'admin/accounting',
    Component: () => (
      <AdminRoute>
        <AdminAccountingPage />
      </AdminRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Admin Accounting route');
      return null;
    },
  },
  {
    // Admin only. The charge-type map is global: one edit changes the QuickBooks
    // item that every later billing activation writes, for every organization, and
    // the write is an adminProcedure. A customer who reached this screen could not save.
    path: 'admin/income-charge-mapping',
    Component: () => (
      <AdminRoute>
        <IncomeChargeMappingPage />
      </AdminRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Admin Income Charge Mapping route');
      return null;
    },
  },
  {
    path: 'admin/payments',
    Component: () => (
      <AdminRoute>
        <AdminPaymentsPage />
      </AdminRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Admin Payments route');
      return null;
    },
  },
  {
    // Guard must stay RestrictedBoardMemberRoute, NOT AdminRoute — the
    // AP Exception Queue feature spec (ap-exception-queue.feature) is
    // explicitly "As a property manager", and its E2E suite logs in as
    // a regular (non-admin) entity user. AdminRoute here would break
    // that PM-facing capability and its passing E2E coverage.
    //
    // This route is intentionally kept but NOT linked from the Invoices
    // menu: UAT wants Invoices = AR/AP only, so the "Payment Exceptions"
    // nav child was removed in Layout.tsx (a hide, not a retirement).
    // Do not re-add that child to "fix" discoverability — the queue is
    // reached by URL, and ap-exception-queue.feature covers it via goto.
    path: 'admin/payments/exceptions',
    Component: () => (
      <RestrictedBoardMemberRoute>
        <ExceptionQueuePage />
      </RestrictedBoardMemberRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Admin Payment Exceptions route');
      return null;
    },
  },
  {
    path: 'admin/rfp-templates',
    Component: () => (
      <AdminRoute>
        <AdminRfpTemplatesPage />
      </AdminRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Admin RFP Templates route');
      return null;
    },
  },
  {
    // Admin only. The chart of accounts is global: a line added, renamed or retired here
    // reaches every organization, and every procedure behind the screen is an adminProcedure.
    path: 'admin/chart-lines',
    Component: () => (
      <AdminRoute>
        <ChartLinesPage />
      </AdminRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Admin Chart Lines route');
      return null;
    },
  },
  {
    // Admin only until the switch-over. Every procedure behind this screen is an adminProcedure.
    path: 'admin/onboarding/:ownershipId?/:stepId?',
    Component: () => (
      <AdminRoute>
        <OnboardingPage />
      </AdminRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Admin Onboarding route');
      return null;
    },
  },
  {
    // Admin only until the switch-over. The settings status is an adminProcedure; the Permissions
    // tab opens today's window, which keeps its own checks.
    path: 'admin/organizations/:ownershipId?/:tab?',
    Component: () => (
      <AdminRoute>
        <OrganizationPage />
      </AdminRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Admin Organizations route');
      return null;
    },
  },
  {
    path: 'admin/playground',
    Component: () => (
      <AdminRoute>
        <PlaygroundRoute>
          <PlaygroundPage />
        </PlaygroundRoute>
      </AdminRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Admin Playground route');
      return null;
    },
  },
  {
    path: 'incomescheduler',
    Component: () => (
      <ProtectedRoute>
        <IncomeScheduler />
      </ProtectedRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Incomes Scheduler');
      return null;
    },
  },
  {
    path: 'paymentsExample',
    Component: () => (
      <ProtectedRoute>
        <PaymentsExamplePage />
      </ProtectedRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Payments Example route');
      return null;
    },
  },

  // Index route - redirect to home
  {
    index: true,
    Component: () => <Navigate to="/dashboard" replace />,
    loader: async () => {
      logger.log('[Loader] Index route');
      return null;
    },
  },
  // Catch-all route - redirect to home
  {
    path: '*',
    Component: () => <Navigate to="/dashboard" replace />,
    loader: async () => {
      logger.log('[Loader] Catch-all route');
      return null;
    },
  },
];
